import { useEffect, useContext, useState } from "react";
import { ConfigContext } from "../../Context/ConfigContext";
import axios from "axios";
import "./credit.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { OverlayLoader } from "../../Components/Shimmers";
import config from "../../appConfig";
import { axiosGet } from "../../Apis/axiosMethods";
import Config from "../../appConfig";

const CreditStatement = () => {
    const { business_id } = useContext(ConfigContext);

    const [totalDueAmount, setTotalDueAmount] = useState(0);
    const [currentDue, setCurrentDue] = useState(0);

    const [businessInfo, setBusinessInfo] = useState({});
    const [statements, setStatements] = useState([]);

    const [loading, setLoading] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        GetBusinessInfo();
    }, []);

    // =====================================================
    // GET BUSINESS INFO
    // =====================================================
    const GetBusinessInfo = async () => {
        try {
            setLoading(true);

            const response = await axiosGet(`/Users/GetBusinessInfo`, { business_id });

            if (response?.data.success) {
                setBusinessInfo(response?.data.data?.[0] || {});
                setStatements(response?.data.accounts__statement || []);
                setTotalDueAmount(parseFloat(response?.data.total_statement_due_amount || 0));
                setCurrentDue(parseFloat(response?.data.current_due || 0));

            } else {
                console.log(
                    "Error While Fetching Business Info",
                    response?.data.message
                );

                setBusinessInfo({});
                setStatements([]);
                setTotalDueAmount(0);
                setCurrentDue(0);
            }
        } catch (error) {
            console.log(
                "Error fetching business info:",
                error
            );

            setBusinessInfo({});
            setStatements([]);
            setTotalDueAmount(0);
            setCurrentDue(0);
        } finally {
            setLoading(false);
        }
    };

    // =====================================================
    // PRINT
    // =====================================================
    const handlePrint = () => {
        window.print();
    };

    // =====================================================
    // DOWNLOAD PDF
    // =====================================================
    const handleDownloadPDF = async () => {
        try {
            const element = document.querySelector(
                ".credit-statement-container"
            );

            if (!element) return;

            const canvas = await html2canvas(
                element,
                {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                }
            );

            const imgData =
                canvas.toDataURL("image/png");

            const pdf = new jsPDF(
                "p",
                "mm",
                "a4"
            );

            const imgProps =
                pdf.getImageProperties(imgData);

            const pdfWidth =
                pdf.internal.pageSize.getWidth();

            const pdfHeight =
                (imgProps.height * pdfWidth) /
                imgProps.width;

            pdf.addImage(
                imgData,
                "PNG",
                0,
                0,
                pdfWidth,
                pdfHeight
            );

            pdf.save(
                "credit-statement.pdf"
            );
        } catch (error) {
            console.log(
                "Error generating PDF:",
                error
            );
        }
    };

    // =====================================================
    // PRINT CSS
    // =====================================================
    const printStyles = `
        @media print {
            body * {
                visibility: hidden;
            }

            .credit-statement-container,
            .credit-statement-container * {
                visibility: visible;
            }

            .credit-statement-container {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
            }

            .no-print {
                display: none !important;
            }
        }
    `;

    // =====================================================
    // CURRENT STATEMENT
    // =====================================================
    const GetCurrentStatement = () => {
        if (
            !statements ||
            statements.length === 0
        ) {
            return null;
        }

        return statements[
            statements.length - 1
        ];
    };

    const currentStatement =
        GetCurrentStatement();

    // =====================================================
    // FORMAT AMOUNT
    // =====================================================
    const formatAmount = (amount) => {
        return parseFloat(
            amount || 0
        ).toFixed(2);
    };

    // =====================================================
    // FORMAT DATE
    // =====================================================
    const formatDate = (date) => {
        if (!date) {
            return "-";
        }

        const parsedDate =
            new Date(date);

        if (
            isNaN(
                parsedDate.getTime()
            )
        ) {
            return "-";
        }

        return parsedDate
            .toLocaleDateString()
            .replaceAll("/", "-");
    };

    return (
        <>
            {loading ? (
                <div
                    style={{
                        height: "70vh",
                    }}
                >
                    <OverlayLoader />
                </div>
            ) : (
                <main
                    style={{
                        marginTop: "-10px",
                    }}
                >
                    <style>
                        {printStyles}
                    </style>

                    {/* ========================================= */}
                    {/* PRINT / PDF BUTTONS */}
                    {/* ========================================= */}

                    <div
                        className="container mb-3 no-print"
                        style={{
                            position: "absolute",
                            display: "none",
                        }}
                    >
                        <div className="d-flex justify-content-end gap-2">

                            <button
                                onClick={
                                    handlePrint
                                }
                                className="btn btn-light"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    padding: "0",
                                    display:
                                        "inline-flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        "4px",
                                    marginRight:
                                        "8px",
                                }}
                                title="Print Statement"
                            >
                                <i className="fas fa-print"></i>
                            </button>

                            <button
                                onClick={
                                    handleDownloadPDF
                                }
                                className="btn btn-light"
                                style={{
                                    width: "40px",
                                    height: "40px",
                                    padding: "0",
                                    display:
                                        "inline-flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    borderRadius:
                                        "4px",
                                }}
                                title="Download PDF"
                            >
                                <i className="fas fa-file-pdf text-danger"></i>
                            </button>

                        </div>
                    </div>

                    {/* ========================================= */}
                    {/* CREDIT STATEMENT */}
                    {/* ========================================= */}

                    <div className="credit-statement-container">

                        <div className="container py-4">

                            <div className="card shadow-sm">

                                {/* ================================= */}
                                {/* HEADER */}
                                {/* ================================= */}

                                <div className="card-header bg-white border-0">

                                    <div className="row align-items-center">

                                        <div className="col-md-6">

                                            <img
                                                src="/assets/images/Logo/logo_dark.png"
                                                alt="Company Logo"
                                                className="img-fluid"
                                                style={{
                                                    maxWidth:
                                                        "200px",
                                                }}
                                            />

                                        </div>

                                        <div className="col-md-6 text-end">

                                            <h4 className="mb-1 text-primary">
                                                Credit Statement
                                            </h4>

                                        </div>

                                    </div>

                                </div>

                                {/* ================================= */}
                                {/* SUMMARY */}
                                {/* ================================= */}

                                <div className="card-body border-bottom">

                                    {/* BUSINESS INFO */}

                                    <div className="col-md-12">

                                        To,{" "}

                                        <strong>
                                            {
                                                businessInfo?.business_name ||
                                                "-"
                                            }
                                        </strong>

                                        <br />

                                        {
                                            businessInfo?.business_email ||
                                            "-"
                                        }

                                        <br />

                                        {
                                            businessInfo?.business_level_title ||
                                            "-"
                                        }

                                    </div>

                                    {/* SUMMARY CARDS */}

                                    <div className="row mt-2">

                                        {/* PAYMENT DUE DATE */}

                                        <div className="col-md-4">

                                            <div className="summary-box p-3">

                                                <h6 className="text-muted mb-1">
                                                    Payment due date
                                                </h6>

                                                <h4 className="mb-0 text-danger">

                                                    {currentStatement
                                                        ? formatDate(
                                                            currentStatement.due_date
                                                        )
                                                        : "-"}

                                                </h4>

                                            </div>

                                        </div>

                                        {/* AMOUNTS */}

                                        <div className="col-md-8">

                                            <div className="row">

                                                {/* TOTAL STATEMENT DUE */}

                                                <div className="col">

                                                    <div className="summary-box p-3">

                                                        <h6 className="text-muted mb-1">
                                                            Total Statement Due Amount
                                                        </h6>

                                                        <h4 className="mb-0 text-danger">
                                                            {formatAmount(totalDueAmount)} {config.currency}

                                                        </h4>

                                                    </div>

                                                </div>

                                                {/* CURRENT DUE */}

                                                <div className="col">

                                                    <div className="summary-box p-3">

                                                        <h6 className="text-muted mb-1">
                                                            Current Due Amount
                                                        </h6>

                                                        <h4 className="mb-0 text-danger">
                                                            {formatAmount(currentDue)} {config.currency}

                                                        </h4>

                                                    </div>

                                                </div>

                                                {/* CREDIT LIMIT */}

                                                <div className="col">

                                                    <div className="summary-box p-3">

                                                        <h6 className="text-muted mb-1">
                                                            Credit Limit
                                                        </h6>

                                                        <h4 className="mb-0 text-danger">
                                                            {formatAmount(businessInfo?.business_credit_limit)} {config.currency}

                                                        </h4>

                                                    </div>

                                                </div>

                                            </div>

                                        </div>

                                    </div>

                                </div>

                                {/* ================================= */}
                                {/* STATEMENTS TABLE */}
                                {/* ================================= */}

                                <div className="card-body">

                                    <div className="table-responsive">

                                        <table className="table table-hover">

                                            <thead className="table-light">

                                                <tr>

                                                    <th>
                                                        Date
                                                    </th>

                                                    <th>
                                                        Statement No.
                                                    </th>

                                                    <th className="text-end">
                                                        Due Amount
                                                    </th>

                                                    <th className="text-end">
                                                        Download
                                                    </th>

                                                </tr>

                                            </thead>

                                            <tbody>

                                                {statements?.length > 0 ? (

                                                    statements.map(
                                                        (
                                                            statement,
                                                            index
                                                        ) => (

                                                            <tr
                                                                key={
                                                                    statement.business_credit_statement_id ||
                                                                    index
                                                                }
                                                            >

                                                                <td>
                                                                    {formatDate(statement.created_date)}
                                                                </td>

                                                                <td>

                                                                    <span className="badge bg-light text-dark">

                                                                        #{statement?.credit_statement_id}

                                                                    </span>

                                                                </td>

                                                                <td className="text-end">

                                                                    <span className="fw-bold text-danger">
                                                                        {formatAmount(statement.total_amount)} {Config.currency}

                                                                    </span>

                                                                </td>

                                                                <td className="text-end">

                                                                    {statement.credit_statement ? (

                                                                        <a
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            href={`${Config?.statement_api}/public/statements/${statement.credit_statement}`}
                                                                            className="btn btn-link"
                                                                        >
                                                                            Download
                                                                        </a>

                                                                    ) : (
                                                                        "-"
                                                                    )}

                                                                </td>

                                                            </tr>

                                                        )
                                                    )

                                                ) : (

                                                    <tr>

                                                        <td
                                                            colSpan="4"
                                                            className="text-center text-muted py-4"
                                                        >
                                                            No statements found
                                                        </td>

                                                    </tr>

                                                )}

                                            </tbody>

                                        </table>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </main>
            )}
        </>
    );
};

export default CreditStatement;